<template>
  <div></div>
</template>
<script>

import { setFSmessage, setFSthread } from "@/services/messages.js";
import * as emailApi from "@/services/email_interface.js";
import * as emailCache from "@/services/email_cache.js";
import * as emailSearch from "@/services/email_search.js";
import { syncMailbox } from "@/services/email_sync.js";
import { extractAttachmentsText } from "@/services/email_attachments.js";
import { prepMatchingPatterns, processMessage } from "@/services/email_matching.js";
import { getMember } from "@/services/teams.js";
import { useEmailSettingsStore } from '@/stores/email_settings_store';
import { usePatentStore } from '@/stores/patent_store';
import { useTrademarkStore } from '@/stores/trademark_store';
import { mapStores } from 'pinia';


export default {
  components: {
  },
  data() {
    return {
      userId: null,
      firmId: null,
      processing_stopped: false,
      patterns: null,
      loopRunning: false,
      counter: 0,
    };
  },
  computed: {
    emailSettings() {
      return this.emailSettingsStore.settings;
    },
    processingStatus() {
      return this.emailSettingsStore.processing_status;
    },
    ...mapStores(useEmailSettingsStore, usePatentStore, useTrademarkStore),
  },
  async mounted() {
    this.userId = this.$cookies.get("uid");
    this.firmId = this.$cookies.get("defaultFirm");
    this.emailSettingsStore.subscribeToSettings(this.firmId, this.userId);
    //TODO normalize this so every method takes firmId, userId.  NOT userId, firmId
    await this.patentStore.loadPatents(this.userId, this.firmId);
    // trademarks are staff-only (rules) — the trademark store's listeners are
    // denied for client-role members, so skip the load entirely. Clients CAN
    // have assigned patents, so the patent store still loads for them.
    const member = await getMember(this.firmId, this.userId).catch(() => null);
    if (member?.role !== "client") {
      await this.trademarkStore.loadData(this.firmId, this.userId);
    }
  },
  watch: {
    processingStatus(newStatus) {
      if (newStatus == "started") {
        this.runAutoLoop();
      } else if (newStatus == "stopped") {
        this.processing_stopped = true;
      }
    },
  },
  methods: {
    // Automatic mode: while processing_status stays 'started', sync the mailbox
    // into the local cache and drain the processing backlog, then re-sync
    // periodically. The loop wakes early when syncNonce changes (horizon
    // widened, processing resumed). Sync keeps running while processing is
    // paused; processing is skipped when paused or when the tab is hidden.
    async runAutoLoop() {
      if (this.loopRunning) {
        return;
      }
      this.loopRunning = true;
      const RESYNC_INTERVAL_MS = 5 * 60 * 1000;
      try {
        while (this.emailSettingsStore.processing_status === 'started') {
          if (emailApi.alreadySignedIn()) {
            this.processing_stopped = false;
            const nonceAtStart = this.emailSettingsStore.syncNonce;
            try {
              await this.startSync();
            } catch (e) {
              // keep the loop alive; the next cycle retries
              console.error('sync/process cycle failed', e);
              this.emailSettingsStore.setSyncStatus('error', null, e);
            }
            // sleep until the next cycle, waking early on stop or nonce change
            const wakeAt = Date.now() + RESYNC_INTERVAL_MS;
            while (Date.now() < wakeAt &&
                   this.emailSettingsStore.processing_status === 'started' &&
                   this.emailSettingsStore.syncNonce === nonceAtStart) {
              await new Promise(r => setTimeout(r, 5000));
            }
          } else {
            // not signed in yet; check again shortly
            await new Promise(r => setTimeout(r, 5000));
          }
        }
      } finally {
        this.loopRunning = false;
      }
    },
    async startSync() {
      let horizonTsSec = this.emailSettingsStore.timeframe;
      if (!horizonTsSec) {
        // if no timeframe is set, default to 1 month
        const now = new Date();
        const oneMonthsAgo = new Date();
        oneMonthsAgo.setMonth(now.getMonth() - 1);
        horizonTsSec = Math.floor(oneMonthsAgo.getTime() / 1000);
        this.emailSettingsStore.setTimeframe(horizonTsSec);
      }
      this.patterns = await prepMatchingPatterns({
        patentStore: this.patentStore,
        trademarkStore: this.trademarkStore,
        emailSettings: this.emailSettings,
        updateSettings: (settings) => this.emailSettingsStore.updateSettings(settings),
      });
      // mirror the mailbox into the local IndexedDB cache, then process the
      // cached (unprocessed) messages — no provider paging during processing
      const syncResult = await syncMailbox({
        horizonTsSec,
        shouldStop: () => this.processing_stopped,
        onProgress: (s) => this.emailSettingsStore.setSyncStatus(s.status, s.syncedThisRun, s.error),
      });
      this.emailSettingsStore.setSyncStatus(syncResult.status, syncResult.syncedThisRun, syncResult.error);
      if (syncResult.syncedThisRun > 0) {
        emailSearch.markDirty();
      }
      await this.refreshUnprocessedCount();
      if (!this.emailSettingsStore.processingPaused) {
        await this.processCachedMessages();
      }
    },
    async refreshUnprocessedCount() {
      const n = await emailCache.countUnprocessedMessages().catch(() => 0);
      this.emailSettingsStore.setUnprocessedCount(n);
    },
    // Stage-0 provider for processMessage: fetches and parses attachment
    // content on demand (only reached when body/subject/filenames matched
    // nothing), then caches the extracted TEXT on the message record so each
    // attachment is parsed exactly once across sessions and rule re-scans.
    async attachmentTextProvider(entry, message, atts) {
      if (entry.attachmentsParsed) {
        return entry.attachmentText || '';
      }
      const text = await extractAttachmentsText(message, atts, emailApi);
      await emailCache.updateMessage(entry.id, {
        attachmentsParsed: true,
        attachmentText: text.slice(0, 200000),
      });
      if (text) {
        emailSearch.markDirty();
      }
      return text;
    },
    // Processes cached messages that haven't been processed with the current
    // rule-set version. Content is read from IndexedDB — no provider calls.
    async processCachedMessages() {
      this.emailSettingsStore.resetCounter();

      let chunk = [];
      do {
        if (this.processing_stopped || this.emailSettingsStore.processingPaused) {
          return;
        }
        chunk = await emailCache.getUnprocessedMessages(200, 0);
        if (!chunk || chunk.length === 0) {
          break;
        }

        for (const entry of chunk) {
          // Check if stopped/paused and return if true
          if (this.processing_stopped || this.emailSettingsStore.processingPaused) {
            return;
          }

          const categories = await processMessage(
            entry.raw,
            this.patterns,
            emailApi,
            (message, atts) => this.attachmentTextProvider(entry, message, atts)
          );
          this.emailSettingsStore.increaseCounter();
          if (categories) {
            // Ensure idempotent storage
            await setFSmessage(this.firmId, this.userId, entry.id, categories);
            await setFSthread(this.firmId, this.userId, entry.threadId, categories);
          }
        }

        await emailCache.markMessagesProcessed(chunk.map((e) => e.id));
        await this.refreshUnprocessedCount();
      } while (chunk.length > 0);

      return;
    },
  }
}
</script>
